import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SelectionModel } from '@angular/cdk/collections';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatMenuModule } from '@angular/material/menu';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { NumeralPipe } from '@pipes/numeral.pipe';
import { DatetimeRangePickerComponent } from '@components/shared/datetime-range-picker/datetime-range-picker.component';
import { Role } from '../log-list.component';
import {
  ADMIN_TABLE_COLUMN_LIST,
  GENERAL_TABLE_COLUMN_LIST,
  SYSTEM_ADMIN_TABLE_COLUMN_LIST,
} from './log-list-table';
import { DirectivesModule } from 'src/app/directive/auto-complete-off/auto-complete-off.module';

/** AuditログのViewModel */
export type AuditLogViewModel = {
  /** 操作種別 */
  operationType: string;
  /** 操作ユーザー */
  userId: string;
  /** メッセージ */
  message: string;
  /** 日時 */
  date: Date;
};

@Component({
  selector: 'app-audit-log-list-table',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    DatePipe,
    MatMenuModule,
    MatCardModule,
    NumeralPipe,
    DatetimeRangePickerComponent,
    DirectivesModule,
  ],
  templateUrl: './log-list-table.component.html',
  styleUrl: './log-list-table.component.scss',
})
export class AuditLogListTableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  /** Auditログ */
  @Input({ required: true }) auditLog: unknown;

  /** Auditログ一覧 */
  @Input({ required: true }) auditLogList: AuditLogViewModel[] = [];

  /** 操作種別一覧 */
  @Input() operationTypeList: string[] = [];

  /** 権限 */
  @Input({ required: true }) role: Role = 'system-admin';

  /** 現在のテナント */
  @Input() currentTenant: string = '';

  /** 閲覧のみか */
  @Input() readonly = false;

  /** デバイス詳細設定の更新を通知 */
  @Output() openDeviceEditDialog = new EventEmitter<void>();

  /** ページネーション要素 */
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** ソート要素 */
  @ViewChild(MatSort) sort!: MatSort;

  /** カラム */
  public displayedColumns: string[] = ADMIN_TABLE_COLUMN_LIST;

  /** 一覧情報 */
  public dataSource = new MatTableDataSource<AuditLogViewModel>([]);

  /** 選択状態 */
  @Input() selection = new SelectionModel<AuditLogViewModel>(true, []);

  /** 操作種別検索キーワード */
  public operationTypeKeyword = '';

  /** 操作ユーザー検索キーワード */
  public userIdKeyword = '';

  /** メッセージ検索キーワード */
  public messageKeyword = '';

  /** 日付検索キーワード */
  public dateKeyword = '';

  /** 選択された操作種別 */
  public selectedOperationType: string | undefined;

  /** 選択された操作ユーザー */
  public selectedUserId: string | undefined;

  now = new Date().setDate(new Date().getDate() - 1);

  options = [
    {
      name: 'operationType',
      checked: true,
      disabled: true,
      displayName: '操作種別',
    },
    {
      name: 'userId',
      checked: true,
      disabled: false,
      displayName: '操作ユーザー',
    },
    {
      name: 'message',
      checked: true,
      disabled: false,
      displayName: 'メッセージ',
    },
    { name: 'date', checked: true, disabled: false, displayName: '日時' },
  ];
  constructor() {}

  public ngOnInit(): void {
    this._setDisplayedColumns(this.role);
    this.updateDisplayList();

    this.applyFilter();
  }

  public ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    // フィルター設定
    this.dataSource.filterPredicate = (element: AuditLogViewModel) => {
      let operationType = true;
      if (this.selectedOperationType) {
        // operationTypeKeywordが空でない場合のみフィルタリングを行う
        operationType =
          element.operationType === undefined
            ? false
            : element.operationType === this.selectedOperationType;
      }

      let userId = true;
      if (this.userIdKeyword) {
        // userIdKeywordが空でない場合のみフィルタリングを行う
        userId =
          element.userId
            .trim()
            .toLowerCase()
            .indexOf(this.userIdKeyword.toLowerCase()) > -1;
      }

      let message = true;
      if (this.messageKeyword) {
        // messageKeywordが空でない場合のみフィルタリングを行う
        message =
          element.message
            .trim()
            .toLowerCase()
            .indexOf(this.messageKeyword.toLowerCase()) > -1;
      }

      const date = true;

      return operationType && userId && message && date;
    };
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['auditLogList'] !== undefined) {
      const auditLogList: AuditLogViewModel[] =
        changes['auditLogList'].currentValue;
      this.dataSource.data = auditLogList;
    } else if (changes['role'] !== undefined) {
      const role: Role = changes['role'].currentValue;
      this._setDisplayedColumns(role);
    }
  }

  /**
   * すべての行を選択／非選択に切り替える
   */
  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.filteredData);
  }

  /**
   * すべて選択されているか
   *
   * @returns すべて選択されていればtrue, 非選択があればfalse
   */
  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.filteredData.length;
    return numSelected === numRows;
  }

  /**
   * 検索条件（FilterPredicate）に基づいて、フィルタリング処理を適用する。
   */
  public applyFilter(): void {
    // フィルタ処理を発火
    // ※空文字指定だとフィルタリングがされないためdummy文字列を指定している
    // （Angular materialのtable-data-source.tsの_filterDataメソッド参照）
    this.dataSource.filter = 'dummy';
  }

  /**
   * 操作種別でのフィルタリングをクリアする。
   */
  public clearOperationTypeFilter(): void {
    // フィルタ条件をクリア
    this.operationTypeKeyword = '';

    this.applyFilter();
  }

  /**
   * 操作ユーザーでのフィルタリングをクリアする。
   */
  public clearUserIdFilter(): void {
    // フィルタ条件をクリア
    this.userIdKeyword = '';

    this.applyFilter();
  }

  /**
   * メッセージでのフィルタリングをクリアする。
   */
  public clearMessageFilter(): void {
    // フィルタ条件をクリア
    this.messageKeyword = '';

    this.applyFilter();
  }

  /**
   * 権限ごとにテーブルカラムを設定する
   * @param role 権限
   */
  private _setDisplayedColumns(role: Role) {
    if (role === 'system-admin') {
      this.displayedColumns = SYSTEM_ADMIN_TABLE_COLUMN_LIST;
    } else if (role === 'tenant-admin') {
      this.displayedColumns = ADMIN_TABLE_COLUMN_LIST;
    } else {
      this.displayedColumns = GENERAL_TABLE_COLUMN_LIST;
    }
  }

  /**
   * 表示するリストの項目を設定する
   */
  public updateDisplayList() {
    this.displayedColumns = this.options
      .filter((option) => option.checked)
      .map((option) => option.name);
  }
}
